import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { loyaltyLedgerStatusSchema } from "./accounts.js";
import { loyaltyPointsSchema, loyaltySignedPointsSchema } from "./program.js";
/**
 * Sadakat raporları (ADR 0017 §24, #68, #69): puan yükümlülüğü (açık partiler × güncel puan değeri ↔ defterdeki
 * `LOYALTY_LIABILITY` hesabı) ve dönem özeti. Kişisel veri taşımaz.
 */
const pointsValueSchema = z.object({ points: loyaltySignedPointsSchema, value: moneySchema });
export const LOYALTY_AGING_BUCKETS = ["DAYS_0_30", "DAYS_31_90", "DAYS_91_180", "DAYS_181_PLUS"];
export const loyaltyAgingBucketSchema = z.enum(LOYALTY_AGING_BUCKETS);
export const LOYALTY_AGING_BUCKET_LABELS = {
    DAYS_0_30: "≤ 30 gün (süresi dolan dâhil)",
    DAYS_31_90: "31–90 gün",
    DAYS_91_180: "91–180 gün",
    DAYS_181_PLUS: "180 günden fazla",
};
export const loyaltyLiabilityQuerySchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema
        .optional()
        .describe("Defter bakiyesinin kesim günü ve yaşlandırma referansı; boşsa bugün"),
});
export const loyaltyLiabilityReportSchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema,
    generatedAt: timestampSchema,
    pointValue: z.string(),
    outstanding: pointsValueSchema.describe("Şirketin istasyonlarında KAZANILMIŞ, açık partilerdeki puan (şu an)"),
    byStation: z.array(z.object({
        stationId: uuidSchema,
        stationName: z.string(),
        points: loyaltyPointsSchema,
        value: moneySchema,
    })),
    aging: z.array(z.object({ bucket: loyaltyAgingBucketSchema, points: loyaltyPointsSchema, value: moneySchema })),
    pendingReview: pointsValueSchema.describe("İncelemede bekleyen kazanım (bakiyeye ve deftere girmedi)"),
    negativeBalances: z
        .object({ accounts: z.number().int(), points: loyaltySignedPointsSchema, value: moneySchema })
        .describe("Eksi bakiyeli hesaplar (kiracı geneli; puan hesabı şirkete bağlı değildir)"),
    unclosedValue: moneySchema.describe("Bu şirketin gün kapanışını bekleyen kazanım − geri alma − süre sonu değeri (henüz deftere yazılmadı)"),
    ledger: z
        .object({
        accountId: uuidSchema,
        accountCode: z.string(),
        accountName: z.string(),
        balance: moneySchema.describe("Alacak − borç, `asOf` dâhil kesinleşmiş fişler"),
    })
        .nullable()
        .describe("`LOYALTY_LIABILITY` eşleştirmesi yoksa null"),
    difference: moneySchema.nullable().describe("outstanding.value − ledger.balance"),
    warnings: z.array(z.string()),
});
export const loyaltySummaryQuerySchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    companyId: uuidSchema.optional(),
});
export const loyaltySummaryTotalsSchema = z.object({
    earned: pointsValueSchema,
    redeemed: pointsValueSchema,
    expired: pointsValueSchema,
    reversed: pointsValueSchema,
    restored: pointsValueSchema,
    adjusted: pointsValueSchema,
    pendingReview: pointsValueSchema,
});
export const loyaltySummarySchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    companyId: uuidSchema.nullable(),
    totals: loyaltySummaryTotalsSchema,
    byStation: z.array(loyaltySummaryTotalsSchema.extend({
        stationId: uuidSchema.nullable(),
        stationName: z.string().nullable(),
        companyId: uuidSchema,
    })),
    byBusinessUnit: z
        .array(z.object({ businessUnit: businessUnitTypeSchema.nullable(), earnedPoints: loyaltyPointsSchema }))
        .describe("İşlenmiş kazanımın satır kırılımından (iş birimi)"),
    activeMembers: z.number().int().describe("Dönemde en az bir işlenmiş hareketi olan müşteri"),
    tierDistribution: z.array(z.object({
        tierCode: z.string().nullable(),
        tierName: z.string().nullable(),
        accounts: z.number().int(),
    })),
});
// ── Gün kapanışı ─────────────────────────────────────────────────────────────
export const loyaltyDailyClosingSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    businessDate: localDateSchema,
    earnValue: moneySchema,
    reversalValue: moneySchema,
    expireValue: moneySchema,
    entryCount: z.number().int(),
    lines: z.array(z.object({
        entryType: z.enum(["EARN", "REVERSAL", "EXPIRE"]),
        businessDate: localDateSchema,
        points: loyaltySignedPointsSchema,
        value: moneySchema,
        count: z.number().int(),
    })),
    journalEntryId: uuidSchema.nullable(),
    ledgerStatus: loyaltyLedgerStatusSchema,
    closedAt: timestampSchema,
    closedBy: uuidSchema.nullable(),
});
//# sourceMappingURL=reports.js.map